import { Link, useParams } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';
import { categories } from '@/mocks/categories';
import { topics } from '@/mocks/topics';
import Breadcrumbs from '@/components/feature/Breadcrumbs';
import TopicCard from '@/components/feature/TopicCard';

const DOMAIN = 'https://jianxiaoer.cn';

export default function CategoryPage() {
  const { slug = '' } = useParams();
  const category = categories.find((cat) => cat.slug === slug);
  const list = topics.filter((topic) => topic.categorySlug === slug);

  useSEO({
    title: category
      ? `${category.name} - 频道热点与热议话题 | 爆料前线`
      : '频道未找到 | 爆料前线',
    description: category
      ? `${category.name}频道汇总${category.desc}相关的最新热点与热议话题，实时跟进事件进展，共 ${category.count} 条内容持续更新。`
      : '该频道暂不存在，返回首页查看当前正在冲榜的热点话题。',
    keywords: category
      ? `${category.name},${category.name}热点,实时热点,突发新闻,热议排行`
      : '爆料平台,实时热点,突发新闻',
    path: `/category/${slug}`,
    ogType: 'website',
    jsonLd: category
      ? [
          {
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `${category.name}频道`,
            url: `${DOMAIN}/category/${category.slug}`,
            inLanguage: 'zh-CN',
            description: category.desc,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${DOMAIN}/` },
              { '@type': 'ListItem', position: 2, name: category.name, item: `${DOMAIN}/category/${category.slug}` },
            ],
          },
        ]
      : undefined,
  });

  if (!category) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center gap-4 px-4 py-24 text-center md:px-6">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-folder-unknow-line text-3xl" />
        </span>
        <h1 className="font-heading text-2xl font-bold text-foreground-950">找不到这个频道</h1>
        <p className="text-sm text-foreground-600">频道可能已经调整，回首页看看当前正在冲榜的话题吧。</p>
        <Link
          to="/"
          className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto w-full max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: '首页', path: '/' }, { label: category.name }]} />
          <div className="flex flex-wrap items-start gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className={`${category.icon} text-2xl`} />
            </span>
            <div className="min-w-0">
              <h1 className="font-heading text-3xl font-bold text-foreground-950 md:text-4xl">{category.name}</h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-700 md:text-base">{category.desc}</p>
              <p className="mt-3 text-xs text-foreground-500">
                本频道累计收录 <strong className="text-primary-700">{category.count}</strong> 条话题，持续更新中
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-6">
          <div className="mb-6 flex flex-wrap items-center gap-2">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                  cat.slug === slug
                    ? 'bg-primary-500 text-background-50'
                    : 'border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {list.length > 0 ? (
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((topic) => (
                <TopicCard key={topic.slug} topic={topic} categoryName={category.name} />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-inbox-line text-2xl" />
              </span>
              <p className="text-sm text-foreground-700">这个频道暂时还没有收录话题，编辑正在整理中。</p>
              <Link
                to="/ranking"
                className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                先看全网热搜榜
              </Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}